// src/components/TagInput.tsx - FIXED: Proper typing for custom props
import React, { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';
import type { 
  MultiValue, 
  ActionMeta,
  OptionProps,
  MenuListProps,
  MultiValueRemoveProps,
  GroupBase,
  FilterOptionOption
} from 'react-select';
import { Plus, X } from 'lucide-react';
import { useUIConfig } from '../hooks/useAppContext';

interface Tag {
  id: string;
  label: string;
  value: string;
}

interface SuggestionOption {
  value: string;
  count?: number;
}

interface TagOption {
  label: string;
  value: string;
  count?: number;
  __isNew__?: boolean;
}

interface TagInputProps {
  tags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  placeholder?: string;
  suggestions?: SuggestionOption[];
  maxTags?: number;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
  showCounts?: boolean;
  maxSuggestions?: number;
  fieldName?: string; // Add fieldName prop to identify phone fields
}

const MultiValueRemove = (props: MultiValueRemoveProps<TagOption, true, GroupBase<TagOption>>) => {
  return (
    <components.MultiValueRemove {...props}>
      <X size={12} />
    </components.MultiValueRemove>
  );
};

// FIXED: Proper typing for Option component with custom props
const Option = (props: OptionProps<TagOption, true, GroupBase<TagOption>> & { showCounts?: boolean }) => {
  const { showCounts } = props;
  
  if (props.data.__isNew__) {
    return (
      <components.Option {...props}>
        <div className="create-option">
          <Plus size={14} className="create-option-icon" />
          <span>{props.children}</span>
        </div>
      </components.Option>
    );
  }
  return (
    <components.Option {...props}>
      <div className="suggestion-option">
        <span className="suggestion-value">{props.data.label}</span>
        {showCounts && props.data.count && (
          <span className="suggestion-count">
            {props.data.count.toLocaleString()}
          </span>
        )}
      </div>
    </components.Option>
  );
};

const MenuList = (props: MenuListProps<TagOption>) => {
  return (
    <components.MenuList {...props}>
      {props.children}
    </components.MenuList>
  );
};

const TagInput: React.FC<TagInputProps> = ({
  tags,
  onTagsChange,
  placeholder = "Type to add tags...",
  suggestions = [],
  maxTags = 20,
  className = "",
  isLoading = false,
  disabled = false,
  showCounts = true,
  maxSuggestions,
  fieldName
}) => {
  const uiConfig = useUIConfig();
  
  const effectiveMaxSuggestions = maxSuggestions ?? uiConfig.maxSuggestions;

  // Function to clean phone numbers by removing special characters
  const cleanPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters except for the plus sign at the beginning
    return phone.replace(/[^\d+]/g, '');
  };

  // Function to check if this is a phone field
  const isPhoneField = (): boolean => {
    return fieldName === 'PHONES' || fieldName === 'MOBILE_PHONES';
  };

  const tagOptions = useMemo(() => 
    tags.map(tag => ({ label: tag.label, value: tag.value })),
    [tags]
  );

  const fullSuggestionOptions = useMemo(() => {
    const availableSuggestions = suggestions.filter(suggestion => 
      !tags.some(tag => tag.value.toLowerCase() === suggestion.value.toLowerCase())
    );
    
    return availableSuggestions.map(suggestion => ({ 
      label: suggestion.value, 
      value: suggestion.value,
      ...(showCounts && { count: suggestion.count })
    }));
  }, [suggestions, tags, showCounts]);

  const displaySuggestionOptions = useMemo(() => {
    return fullSuggestionOptions.slice(0, effectiveMaxSuggestions);
  }, [fullSuggestionOptions, effectiveMaxSuggestions]);

  const handleChange = (
    newValue: MultiValue<TagOption>,
    actionMeta: ActionMeta<TagOption>
  ) => {
    if (actionMeta.action === 'select-option' || actionMeta.action === 'create-option') {
      if (newValue.length <= maxTags) {
        const newTags = newValue.map((option, index) => {
          let value = option.value;
          // Clean phone numbers if this is a phone field and the action is create-option (user pressed enter)
          if (isPhoneField() && actionMeta.action === 'create-option') {
            value = cleanPhoneNumber(value);
          }
          return {
            id: `${Date.now()}-${index}`,
            label: value, // Use cleaned value for label too
            value: value
          };
        });
        onTagsChange(newTags);
      }
    } else if (actionMeta.action === 'remove-value') {
      const newTags = newValue.map((option, index) => ({
        id: `${Date.now()}-${index}`,
        label: option.label,
        value: option.value
      }));
      onTagsChange(newTags);
    } else if (actionMeta.action === 'clear') {
      onTagsChange([]);
    }
  };

  const formatCreateLabel = (inputValue: string) => `Add "${inputValue}"`;

  const noOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if (inputValue.length < 2) {
      return `Type at least 2 characters to search...`;
    }
    return `No matches found for "${inputValue}"`;
  };

  const customFilterOption = (option: FilterOptionOption<TagOption>, inputValue: string) => {
    if (option.data.__isNew__) return true;
    
    if (!inputValue || inputValue.length < 2) return true;
    
    const searchTerm = inputValue.toLowerCase();
    const matchesSearch = option.label.toLowerCase().includes(searchTerm);
    
    if (matchesSearch) {
      const isInFullDataset = fullSuggestionOptions.some(
        fullOption => fullOption.value.toLowerCase() === option.value.toLowerCase()
      );
      return isInFullDataset;
    }
    
    return false;
  };

  return (
    <div className={`enhanced-tag-input ${className}`}>
      <CreatableSelect<TagOption, true, GroupBase<TagOption>>
        isMulti
        value={tagOptions}
        onChange={handleChange}
        options={displaySuggestionOptions}
        placeholder={tags.length >= maxTags ? `Maximum ${maxTags} tags reached` : placeholder}
        isDisabled={disabled || tags.length >= maxTags || isLoading}
        isLoading={isLoading}
        formatCreateLabel={formatCreateLabel}
        createOptionPosition="first"
        classNamePrefix="react-select"
        className="react-select-container"
        noOptionsMessage={noOptionsMessage}
        filterOption={customFilterOption}
        menuIsOpen={undefined}
        maxMenuHeight={300}
        menuShouldScrollIntoView={false}
        isSearchable={true}
        isClearable={false}
        components={{
          MultiValueRemove,
          // FIXED: Pass showCounts properly to Option component
          Option: (props) => <Option {...props} showCounts={showCounts} />,
          MenuList
        }}
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: '40px',
            border: `1px solid ${state.isFocused ? 'var(--primary)' : 'var(--gray-200)'}`,
            borderRadius: 'var(--radius)',
            backgroundColor: disabled ? 'var(--gray-100)' : 'var(--gray-0)',
            opacity: disabled ? 0.7 : 1,
            boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
            '&:hover': {
              borderColor: state.isFocused ? 'var(--primary)' : 'var(--gray-300)'
            }
          }),
          menu: (provided) => ({
            ...provided,
            zIndex: 100,
            border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg)'
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
              ? 'var(--primary)' 
              : state.isFocused 
                ? 'var(--primary-light)' 
                : 'var(--gray-0)',
            color: state.isSelected 
              ? 'white' 
              : state.isFocused 
                ? 'var(--primary)' 
                : 'var(--gray-700)',
            padding: 'var(--s2) var(--s3)',
            fontSize: 'var(--text-sm)',
            cursor: 'pointer'
          }),
          menuList: (provided) => ({
            ...provided,
            maxHeight: 300,
            overflowY: 'auto'
          }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'var(--primary-light)',
            borderRadius: 'var(--radius-tag)',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            color: 'var(--primary)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            padding: '2px 6px'
          }),
          multiValueRemove: (provided) => ({
            ...provided,
            color: 'var(--primary)',
            borderRadius: 'var(--radius-tag-remove)',
            padding: '2px',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(59, 130, 246, 0.2)'
            }
          }),
          placeholder: (provided) => ({
            ...provided,
            color: 'var(--gray-400)',
            fontSize: 'var(--text-sm)'
          }),
          input: (provided) => ({
            ...provided,
            color: 'var(--gray-900)',
            fontSize: 'var(--text-sm)'
          })
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: 'var(--primary)',
            primary75: 'var(--primary)',
            primary50: 'var(--primary-light)',
            primary25: 'var(--primary-light)'
          }
        })}
      />
    </div>
  );
};

export default TagInput;